import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget for public forms.
 * @param {string} siteKey
 */
export function useTurnstile(siteKey) {
  const hostRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [token, setToken] = useState("");
  const [ready, setReady] = useState(false);
  const enabled = Boolean(siteKey) && String(siteKey).length > 10;

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      setToken("");
      return undefined;
    }

    let cancelled = false;

    function renderWidget() {
      if (cancelled || !hostRef.current || !window.turnstile) return;
      if (widgetIdRef.current != null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(hostRef.current, {
        sitekey: siteKey,
        callback: (t) => setToken(String(t ?? "")),
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
      });
      setReady(true);
    }

    if (window.turnstile) {
      renderWidget();
      return () => {
        cancelled = true;
        if (widgetIdRef.current != null && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            /* ignore */
          }
        }
      };
    }

    const existing = document.querySelector('script[data-talo-turnstile="1"]');
    if (existing) {
      existing.addEventListener("load", renderWidget);
      return () => {
        cancelled = true;
        existing.removeEventListener("load", renderWidget);
      };
    }

    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.dataset.taloTurnstile = "1";
    script.onload = renderWidget;
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
    };
  }, [enabled, siteKey]);

  function reset() {
    setToken("");
    if (widgetIdRef.current != null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* ignore */
      }
    }
  }

  return { hostRef, token, ready, reset, enabled };
}
